import { supabase } from "./supabase";
import { cambiarTarjetaDeMarca, fetchRebillCardUpdateLink, fetchStripeBillingPortalUrl } from "./paymentServer";
import {
  estadoDeCobroDeMarcas,
  type BilleteraParaCobro,
  type EstadoDeCobro,
  type PlataformaDePago,
} from "./estadoDeCobro";
import { cuentaYPermisos } from "./permisosDelEquipo";
import { mensajeDeErrorAlCambiarTarjetaDeMarca } from "../components/facturacionPorMarca/planesPorMarca";

/**
 * Lectura y acciones del aviso de cobro rechazado (ADR-2026-10-26-cobros-rechazados-en-la-plataforma).
 */

export interface ColumnasDeRechazo {
  cobro_rechazado_at?: string | null;
  cobro_proximo_reintento?: string | null;
  cobro_motivo_rechazo?: string | null;
  cobro_tarjeta_ultimos4?: string | null;
}

/**
 * Las columnas `cobro_*` en una consulta APARTE: si la migración todavía no se aplicó, PostgREST
 * rechaza el select entero, y meterlas en el select de la pantalla la rompería. Ante cualquier
 * error devuelve `{}` (= sin rechazo en curso) y la pantalla sigue como siempre.
 */
export async function leerRechazoDeCobro(idCompany: string): Promise<ColumnasDeRechazo> {
  try {
    const { data, error } = await supabase
      .from("company_profiles")
      .select("cobro_rechazado_at, cobro_proximo_reintento, cobro_motivo_rechazo, cobro_tarjeta_ultimos4")
      .eq("id_company", idCompany)
      .maybeSingle();
    if (error || !data) return {};
    return data as ColumnasDeRechazo;
  } catch {
    return {};
  }
}

/** Donde el cliente de MercadoPago administra sus suscripciones (la tarjeta no se cambia por API). */
export const URL_SUSCRIPCIONES_MERCADOPAGO = "https://www.mercadopago.com.ar/subscriptions";

/**
 * Lleva al cliente a cambiar la tarjeta con la que se le cobra:
 *  - Rebill: página hosteada de Rebill (no cobra; la sub queda con la tarjeta nueva).
 *  - Stripe: billing portal.
 *  - MercadoPago: su cuenta de MP, en otra pestaña (decisión de Matías: no se lo pasa a Rebill). La
 *    app pasa antes por `ModalTarjetaMercadoPago`, que explica dónde está "Suscripciones" en MP.
 * Rebill y Stripe navegan en la misma pestaña. Tira Error con un texto mostrable si falla.
 */
export async function abrirCambioDeTarjeta(plataforma: PlataformaDePago): Promise<void> {
  if (plataforma === "mp") {
    window.open(URL_SUSCRIPCIONES_MERCADOPAGO, "_blank", "noopener,noreferrer");
    return;
  }
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Cerrá sesión, volvé a entrar y probá de nuevo.");
  if (plataforma === "rebill") {
    window.location.href = await fetchRebillCardUpdateLink(auth.user.id);
    return;
  }
  if (plataforma === "stripe") {
    window.location.href = await fetchStripeBillingPortalUrl(auth.user.id);
    return;
  }
  throw new Error("No encontramos tu suscripción. Escribinos y lo resolvemos.");
}

// ─── Facturación por marca (ADR-2026-10-29-cobro-rechazado-por-marca) ────────

const COLS_COBRO_DE_MARCA =
  "brand_id, plan, trigger_cancelacion, fecha_cancelacion_programada, cobro_rechazado_at, cobro_proximo_reintento, cobro_motivo_rechazo, cobro_tarjeta_ultimos4";

/**
 * El cobro de las billeteras de marca de la empresa. Las lee cualquiera de la empresa: el RLS de
 * `billeteras_de_marca` deja ver las de sus marcas permitidas (un miembro acotado, sólo las suyas).
 * Tolerante como `leerRechazoDeCobro`: ante cualquier error, `[]` (= sin rechazo en curso).
 */
export async function leerCobroDeBilleteras(idCompany: string): Promise<BilleteraParaCobro[]> {
  try {
    const { data, error } = await supabase
      .from("billeteras_de_marca")
      .select(COLS_COBRO_DE_MARCA)
      .eq("id_company", idCompany);
    if (error || !data) return [];
    return data as BilleteraParaCobro[];
  } catch {
    return [];
  }
}

/** El aviso de cobro de las marcas (el de la más urgente, nombrando las demás). `null` si no hay. */
async function leerCobroDeMarcas(idCompany: string): Promise<EstadoDeCobro | null> {
  const billeteras = await leerCobroDeBilleteras(idCompany);
  const conPlan = billeteras.filter((b) => b.plan);
  if (conPlan.length === 0) return null;
  const nombres = new Map<string, string>();
  let esDueno = false;
  try {
    const [{ data }, cuenta] = await Promise.all([
      supabase.from("brand_profiles").select("id, name").in("id", conPlan.map((b) => b.brand_id)),
      cuentaYPermisos(),
    ]);
    for (const m of (data ?? []) as { id: string; name: string | null }[]) if (m.name) nombres.set(m.id, m.name);
    esDueno = cuenta.esDueno;
  } catch {
    /* sin nombres ni dueño: el aviso igual sale, con "pedile al admin" (el server decide igual) */
  }
  return estadoDeCobroDeMarcas(conPlan, nombres, esDueno);
}

/**
 * El aviso de cobro de la cuenta. Sin facturación por marca devuelve el de la empresa TAL CUAL. Con
 * la bandera, el de las marcas manda (es el que se cobra); si ninguna tiene un rechazo, el de la
 * empresa. Nunca tira.
 */
export async function conCobroDeMarcas(
  deLaEmpresa: EstadoDeCobro | null,
  idCompany: string,
  facturaPorMarca: boolean,
): Promise<EstadoDeCobro | null> {
  if (!facturaPorMarca) return deLaEmpresa;
  try {
    return (await leerCobroDeMarcas(idCompany)) ?? deLaEmpresa;
  } catch {
    return deLaEmpresa;
  }
}

/** Un rechazo tipado de `/marca/cambiar-tarjeta`: el `codigo` deja que la pantalla apague el botón. */
export class ErrorAlCambiarTarjetaDeMarca extends Error {
  readonly codigo: string;
  constructor(codigo: string) {
    super(mensajeDeErrorAlCambiarTarjetaDeMarca(codigo));
    this.codigo = codigo;
  }
}

/**
 * Lleva al dueño a la página hosteada de Rebill para cambiar la tarjeta de UNA marca (misma pestaña).
 * Con un cobro pendiente Rebill lo cobra en el acto con la tarjeta nueva. Tira
 * `ErrorAlCambiarTarjetaDeMarca` ante un rechazo tipado y Error con texto mostrable si falla.
 */
export async function abrirCambioDeTarjetaDeMarca(brandId: string): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new ErrorAlCambiarTarjetaDeMarca("unauthorized");
  const r = await cambiarTarjetaDeMarca({ userId: auth.user.id, brandId });
  if (!r.ok) throw new ErrorAlCambiarTarjetaDeMarca(r.error);
  if (!r.url) throw new Error("No pudimos abrir el cambio de tarjeta. Probá de nuevo en unos minutos.");
  window.location.href = r.url;
}
